<script setup lang="ts">
  import { copyToClipboard, toTitleCase } from '@/utils'
  import {
    ExecutionStatusType,
    getColorForExecutionStatusType
  } from '@/shared/Executions/models/ExecutionStatusType'
  import { computed, ref, watch, onUnmounted } from 'vue'
  import { ExecutionError } from '@/shared/Executions/models/ExecutionError'
  import Badge from '@/components/ui/badge/Badge.vue'
  import { Execution } from '../models/Execution'

  interface IExecutionStatusBadgeProps {
    execution?: Execution
    errors?: Array<ExecutionError>
  }

  const executionStatusType = computed(() => props.execution?.status ?? ExecutionStatusType.Unknown)

  const executionPercent = computed(() => 100) //TODO hook up live percent when available => // props.execution?.progress?.percent ?? 0

  const executionTooltip = computed(() => {
    if (executionStatusType.value === ExecutionStatusType.Failed) {
      return formattedErrors.value || toTitleCase(executionStatusType.value)
    } else if (
      executionStatusType.value !== ExecutionStatusType.Succeeded &&
      executionStatusType.value !== ExecutionStatusType.Unknown
    ) {
      return toTitleCase(props.execution?.statusMessage ?? executionStatusType.value)
    } else {
      return toTitleCase(executionStatusType.value)
    }
  })

  const formattedErrors = computed(() => {
    const errs = props.errors ?? []
    return errs
      .map((e) => `<b>Phase: ${e.phase ?? 'Unknown'}</b>: ${e.message ?? 'Unknown error'}`)
      .join('<br/>')
  })
  const props = defineProps<IExecutionStatusBadgeProps>()

  function handleCopy(e?: Event) {
    e?.stopPropagation()
    copyToClipboard(formattedErrors.value, 'error')
  }

  // --- Progress border animation ---

  const ANIMATING_STATUSES = new Set([
    ExecutionStatusType.Queued,
    ExecutionStatusType.Pending,
    ExecutionStatusType.Throttled,
    ExecutionStatusType.Running,
    ExecutionStatusType.Timeout,
  ])

  const statusShouldAnimate = computed(() => ANIMATING_STATUSES.has(executionStatusType.value))
  const isPaused = ref(false)
  const isAnimating = computed(() => statusShouldAnimate.value && !isPaused.value)

  watch(statusShouldAnimate, (active) => {
    if (!active) isPaused.value = false
  })

  function handleAnimationToggle(e?: Event) {
    if (statusShouldAnimate.value) {
      e?.stopPropagation()
      isPaused.value = !isPaused.value
    }
  }

  function getHexForStatus(status: ExecutionStatusType): string {
    switch (status) {
      case ExecutionStatusType.Running:   return '#3b82f6' // blue-500
      case ExecutionStatusType.Pending:   return '#eab308' // yellow-500
      case ExecutionStatusType.Throttled:
      case ExecutionStatusType.Timeout:   return '#f97316' // orange-500
      default:                            return '#6b7280' // gray-500
    }
  }

  function lightenHex(hex: string, amount: number): string {
    const n = parseInt(hex.slice(1), 16)
    const r = (n >> 16) & 0xff
    const g = (n >> 8) & 0xff
    const b = n & 0xff
    const mix = (c: number) => Math.round(c + (255 - c) * amount)
    return `rgb(${mix(r)},${mix(g)},${mix(b)})`
  }

  function darkenHex(hex: string, amount: number): string {
    const n = parseInt(hex.slice(1), 16)
    const r = (n >> 16) & 0xff
    const g = (n >> 8) & 0xff
    const b = n & 0xff
    const mix = (c: number) => Math.round(c * (1 - amount))
    return `rgb(${mix(r)},${mix(g)},${mix(b)})`
  }

  function blendColors(colorOne: string, colorTwo: string, t: number): string {
    const parseRgb = (s: string): [number, number, number] => {
      if (s.startsWith('#')) {
        const n = parseInt(s.slice(1), 16)
        return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
      }
      const m = s.match(/\d+/g)!
      return [+m[0], +m[1], +m[2]]
    }
    const [rA, gA, bA] = parseRgb(colorOne)
    const [rB, gB, bB] = parseRgb(colorTwo)
    const r = Math.round(rA + (rB - rA) * t)
    const g = Math.round(gA + (gB - gA) * t)
    const b = Math.round(bA + (bB - bA) * t)
    return `rgb(${r},${g},${b})`
  }

  const pulsePosition = ref(0)
  const pulseOpacity = ref(0)
  let intervalId: ReturnType<typeof setInterval> | null = null
  let cycleStart = 0

  // Period scales with arc length so pulse travels at a consistent speed.
  // Calibrated at 75% progress = 2000ms.
  const periodDuration = 2000

  function startPulse() {
    cycleStart = Date.now()
    intervalId = setInterval(() => {
      const period = periodDuration
      // phase: 0 → 1, wraps continuously — position never jumps
      const phase = ((Date.now() - cycleStart) % period) / period
      pulsePosition.value = phase * executionPercent.value
      // sin(π·phase): 0 at both ends, peaks at midpoint — inherently seamless
      pulseOpacity.value = Math.sin(Math.PI * phase)
    }, 50)
  }

  function stopPulse() {
    if (intervalId !== null) {
      clearInterval(intervalId)
      intervalId = null
    }
    pulsePosition.value = 0
    pulseOpacity.value = 0
  }

  watch(isAnimating, (active) => {
    if (active) startPulse()
    else stopPulse()
  }, { immediate: true })

  onUnmounted(stopPulse)

  const PULSE_WIDTH = 8 // percent of arc that the highlight spans

  const progressBorderStyle = computed(() => {
    const hex = getHexForStatus(executionStatusType.value)
    const base = darkenHex(hex, 0.2)
    const highlightFull = lightenHex(hex, 0.35)
    // blend highlight toward base based on pulseOpacity for fade in/out
    const highlight = blendColors(hex, highlightFull, pulseOpacity.value)
    const pct = Math.max(0, Math.min(100, executionPercent.value))
    const pp = pulsePosition.value

    if (pct === 0) {
      return { background: 'transparent' }
    }

    // pulseStart/pulseEnd are unclamped — overflow past pct wraps to the arc start
    const pulseStart = pp
    const pulseEnd = pp + PULSE_WIDTH
    const overflow = pulseEnd - pct

    let stops: string[]

    if (overflow > 0 && pct > PULSE_WIDTH) {
      // Pulse straddles the arc end — split into a wrap segment at [0, overflow]
      // and a tail segment at [pulseStart, pct]
      stops = [
        `${highlight} 0%`,
        `${highlight} ${overflow}%`,
        `${base} ${overflow}%`,
        `${base} ${pulseStart}%`,
        `${highlight} ${pulseStart}%`,
        `${highlight} ${pct}%`,
        `transparent ${pct}%`,
        `transparent 100%`,
      ]
    } else {
      // Normal case — pulse fits within the arc, no wrapping needed
      const clampedStart = Math.max(0, Math.min(pulseStart, pct - PULSE_WIDTH))
      const clampedEnd = Math.min(pct, clampedStart + PULSE_WIDTH)
      stops = [
        `${base} 0%`,
        `${base} ${clampedStart}%`,
        `${highlight} ${clampedStart}%`,
        `${highlight} ${clampedEnd}%`,
        `${base} ${clampedEnd}%`,
        `${base} ${pct}%`,
        `transparent ${pct}%`,
        `transparent 100%`,
      ]
    }

    return {
      background: `conic-gradient(from -90deg, ${stops.join(', ')})`,
    }
  })
</script>

<template>
  <a-tooltip v-if="executionStatusType === ExecutionStatusType.Failed">
    <template #title>
      <span v-html="executionTooltip"></span>
    </template>
    <Badge
      @click="handleCopy"
      class="text-nowrap text-white"
      :class="getColorForExecutionStatusType(executionStatusType)"
    >
      {{ toTitleCase(executionStatusType) }}
    </Badge>
  </a-tooltip>
  <a-tooltip v-if="executionStatusType !== ExecutionStatusType.Failed">
    <template #title>{{ executionTooltip }}</template>
    <div
      v-if="isAnimating"
      class="inline-flex rounded-lg p-[2px]"
      :style="[progressBorderStyle, { cursor: 'pointer' }]"
      @click="handleAnimationToggle"
    >
      <Badge
        class="text-nowrap text-white"
        :class="getColorForExecutionStatusType(executionStatusType)"
      >
        {{ toTitleCase(executionStatusType) }}
      </Badge>
    </div>
    <Badge
      v-else
      class="text-nowrap text-white"
      :class="getColorForExecutionStatusType(executionStatusType)"
      :style="{ cursor: statusShouldAnimate ? 'pointer' : 'default' }"
      @click="handleAnimationToggle"
    >
      {{ toTitleCase(executionStatusType) }}
    </Badge>
  </a-tooltip>
</template>
